import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import type { ArCalendarRow, ArGeoCC, ArGeoCityRaw, ArGeoHi, ArGeoLookups, ArGeoWmap, ArRegistryDetailResponse } from '../../types';
import {
  TIER_NAME, trackerMatch, heurMod, rowMod, phaseNorm, sponsorGroup, facetCounts, PHASES,
  peopleOf, nameKey, KIND_RANK, siteKey, regLabel, regClass, cleanPerson, canonicalCountryMap,
  parseCityEntries, resolveSiteXY,
} from './arHelpers';
import type { SiteGeoContext } from './arHelpers';
import type { ArSharedProps } from './AssetRadarPage';

type DetailState = { status: 'loading' | 'ready' | 'pending' | 'error' | 'missing'; data?: ArRegistryDetailResponse };

function nctOf(r: { nct: string }) { return (String(r.nct || '').match(/NCT\d{8}/i) || [''])[0].toUpperCase(); }

async function pool<T>(items: T[], n: number, fn: (item: T) => Promise<void>) {
  let i = 0;
  await Promise.all(Array.from({ length: Math.min(n, items.length) }, async () => {
    while (i < items.length) { const k = i++; await fn(items[k]); }
  }));
}

interface TrialInfo { id: string; name: string; sponsor: string; phase: string; regimen: string; tier: number; status?: string }
interface SiteAgg {
  key: string; facility: string; city: string; state: string; country: string; zip: string;
  trials: Map<string, TrialInfo>;
  people: Set<string>; recruiting: number;
  byTrial: Map<string, { status: string; contacts: Set<string> }>;
}
interface PersonAgg {
  name: string; trials: Map<string, { id: string; name: string }>; sites: Map<string, SiteAgg>;
  roles: Map<string, string>; rank: number;
}

const StudySitesTab: React.FC<ArSharedProps> = ({ ind, tracker }) => {
  const land = ind?.land || null;
  const calRows = useMemo<(ArCalendarRow & { nct: string })[]>(() => {
    const raw = (ind?.cal || ind?.studies || []) as ArCalendarRow[];
    const seen = new Set<string>(); const out: (ArCalendarRow & { nct: string })[] = [];
    for (const r of raw) {
      const id = nctOf(r);
      if (!id || seen.has(id)) continue;
      seen.add(id); out.push({ ...r, nct: id });
    }
    return out;
  }, [ind]);

  const [regSummary, setRegSummary] = useState<Record<string, { phase?: string; sponsor?: string; status?: string }>>({});
  const [detail, setDetail] = useState<Record<string, DetailState>>({});
  const [geo, setGeo] = useState<{ wmap: ArGeoWmap; lookups: ArGeoLookups } | null>(null);
  const [geoError, setGeoError] = useState(false);
  // Admin-1 borders and the offline city gazetteer (state/province outlines + the city-dot layer that
  // only appears once zoomed in) — fetched lazily alongside wmap/lookups, same cache-for-a-week route.
  const [hi, setHi] = useState<ArGeoHi | null>(null);
  const [cityRaw, setCityRaw] = useState<ArGeoCityRaw | null>(null);
  const [cc, setCc] = useState<ArGeoCC | null>(null);

  // NCTs are pulled from standard of care, approved, and emerging alike by default (tier 1/2/3).
  const [types, setTypes] = useState<Set<number>>(new Set([1, 2, 3]));
  const [agentSel, setAgentSel] = useState(''); const [targetSel, setTargetSel] = useState(''); const [modSel, setModSel] = useState('');
  const [phaseSel, setPhaseSel] = useState(''); const [sponsorSel, setSponsorSel] = useState('');
  const [country, setCountry] = useState(''); const [q, setQ] = useState(''); const [multi, setMulti] = useState(false);
  const [selectedStudies, setSelectedStudies] = useState<Set<string>>(new Set());
  const [cityFilter, setCityFilter] = useState<{ key: string; label: string } | null>(null);
  const [tab, setTab] = useState<'sites' | 'investigators'>('sites');
  const [openSite, setOpenSite] = useState<string | null>(null);
  const [limit, setLimit] = useState(60);
  const [showMap, setShowMap] = useState(true);

  useEffect(() => { setLimit(60); }, [types, agentSel, targetSel, modSel, phaseSel, sponsorSel, country, q, multi, tab, cityFilter]);

  // Reset types to a valid default whenever the tier mix in this setting changes.
  useEffect(() => {
    const present = new Set(calRows.map((r) => r.tier));
    setTypes((prev) => {
      const kept = new Set([...prev].filter((t) => present.has(t as 1 | 2 | 3)));
      if (kept.size) return kept;
      return new Set(present);
    });
  }, [calRows]);

  // Per-row agent / target / modality (self-contained text heuristics; no network calls).
  const agentInfo = useMemo(() => {
    const m = new Map<string, { agent: string; target: string; mod: string }>();
    for (const r of calRows) {
      const text = `${r.regimen} ${r.trial}`;
      const match = trackerMatch(tracker, text);
      const low = text.toLowerCase();
      const la = !match && land ? land.assets.find((a) => { const k = a.name.split(/[\s(+/]/)[0].toLowerCase(); return k.length >= 3 && low.includes(k); }) : null;
      const first = (r.regimen || '').split(/\s+(?:vs\.?|versus)\s+|\s*\+\s*|\s*[;,(]\s*/i)[0];
      const agent = (match && match.asset) || (la && la.name) || first || 'Not stated';
      const target = (match && match.target) || (la && la.moa) || 'Not stated';
      const mod = (match && match.modality) || (la ? heurMod(la.moa) : null) || rowMod(tracker, r);
      m.set(r.nct, { agent, target: target.slice(0, 60), mod });
    }
    return m;
  }, [calRows, tracker, land]);

  // Fetch CT.gov summaries (phase/sponsor, batched) + full details (pooled) for every calendar trial once.
  useEffect(() => {
    if (!calRows.length) { setDetail({}); setRegSummary({}); return; }
    let cancelled = false;
    setDetail(Object.fromEntries(calRows.map((r) => [r.nct, { status: 'loading' as const }])));
    const ids = calRows.map((r) => r.nct);
    (async () => {
      for (let i = 0; i < ids.length; i += 200) {
        try {
          const res = await apiService.arGetRegistry(ids.slice(i, i + 200));
          if (cancelled) return;
          setRegSummary((prev) => ({ ...prev, ...res.trials }));
        } catch { /* ignore; facets fall back to workbook text */ }
      }
    })();
    pool(ids, 4, async (id) => {
      try {
        const d = await apiService.arGetTrial(id);
        if (cancelled) return;
        if (d && (d as any).pending) setDetail((prev) => ({ ...prev, [id]: { status: 'pending' } }));
        else if ((d as any).error) setDetail((prev) => ({ ...prev, [id]: { status: (d as any).error === 'not found' ? 'missing' : 'error' } }));
        else setDetail((prev) => ({ ...prev, [id]: { status: 'ready', data: d } }));
      } catch (e: any) {
        if (cancelled) return;
        setDetail((prev) => ({ ...prev, [id]: { status: e?.response?.status === 404 ? 'missing' : 'error' } }));
      }
    });
    return () => { cancelled = true; };
  }, [calRows]);

  useEffect(() => {
    if (geo || geoError) return;
    Promise.all([apiService.arGetGeoWmap(), apiService.arGetGeoLookups(), apiService.arGetGeoHi(), apiService.arGetGeoCity(), apiService.arGetGeoCC()])
      .then(([wmap, lookups, hiData, cityData, ccData]) => { setGeo({ wmap, lookups }); setHi(hiData); setCityRaw(cityData); setCc(ccData); })
      .catch(() => setGeoError(true));
  }, [geo, geoError]);

  const trialsByType = useMemo(() => calRows.filter((r) => types.has(r.tier)), [calRows, types]);
  const fv = (r: ArCalendarRow & { nct: string }, k: string) => {
    const g = regSummary[r.nct] || {};
    if (k === 'phase') return phaseNorm(g.phase, r.phase);
    if (k === 'sponsor') return sponsorGroup(g.sponsor || r.sponsor);
    return (agentInfo.get(r.nct) as any)?.[k] || '';
  };
  const agentOpts = facetCounts(trialsByType, { agent: agentSel, target: targetSel, mod: modSel }, 'agent', fv);
  const targetOpts = facetCounts(trialsByType, { agent: agentSel, target: targetSel, mod: modSel }, 'target', fv);
  const modOpts = facetCounts(trialsByType, { agent: agentSel, target: targetSel, mod: modSel }, 'mod', fv);
  const phaseOpts = facetCounts(trialsByType, { phase: phaseSel, sponsor: sponsorSel }, 'phase', fv).sort((a, b) => PHASES.indexOf(a[0]) - PHASES.indexOf(b[0]));
  const sponsorOpts = facetCounts(trialsByType, { phase: phaseSel, sponsor: sponsorSel }, 'sponsor', fv);

  const trials = useMemo(() => trialsByType.filter((r) =>
    (!agentSel || fv(r, 'agent') === agentSel) && (!targetSel || fv(r, 'target') === targetSel) && (!modSel || fv(r, 'mod') === modSel) &&
    (!phaseSel || fv(r, 'phase') === phaseSel) && (!sponsorSel || fv(r, 'sponsor') === sponsorSel),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [trialsByType, agentSel, targetSel, modSel, phaseSel, sponsorSel, regSummary]);

  // The "Studies" row: picking specific studies (a multi-select, independent of the agent/target/
  // modality/phase/sponsor facets above) narrows which trials' sites get aggregated below.
  const studyFiltered = useMemo(
    () => (selectedStudies.size ? trials.filter((r) => selectedStudies.has(r.nct)) : trials),
    [trials, selectedStudies],
  );

  const done = studyFiltered.filter((r) => detail[r.nct]?.status === 'ready');
  const pendingN = studyFiltered.filter((r) => detail[r.nct]?.status === 'pending').length;
  const failedN = studyFiltered.filter((r) => ['error', 'missing'].includes(detail[r.nct]?.status || '')).length;

  // A trial's own distinct-site count for its "Studies" chip badge (null while its detail hasn't
  // resolved yet, shown as an ellipsis the same way the chip count loads elsewhere).
  const siteCountFor = (r: ArCalendarRow & { nct: string }): number | null => {
    const d = detail[r.nct];
    if (!d || d.status === 'loading' || d.status === 'pending') return null;
    if (d.status !== 'ready' || !d.data) return 0;
    return new Set((d.data.locations || []).filter((l) => l.facility).map(siteKey)).size;
  };

  // Raw ClinicalTrials.gov country string -> the canonical name wmap.json's map/regions use, so the
  // map, the "Where" select and the table filter all agree on one name per country.
  const countryCanon = useMemo(() => (geo ? canonicalCountryMap(geo) : null), [geo]);

  // wmcity.json's per-country city lists, parsed once (not re-split on every site lookup).
  const cityByIso = useMemo(() => {
    const m = new Map<string, Map<string, [number, number]>>();
    if (!cityRaw) return m;
    for (const [iso2, raw] of Object.entries(cityRaw)) m.set(iso2, parseCityEntries(raw));
    return m;
  }, [cityRaw]);
  const geoCtx = useMemo<SiteGeoContext | null>(() => (
    geo && cc && countryCanon && cityByIso.size
      ? { cc, cityByIso, canon: countryCanon, usSt: geo.lookups.US_ST, gzAlias: geo.lookups.GZ_ALIAS }
      : null
  ), [geo, cc, countryCanon, cityByIso]);

  // Aggregate sites, investigators and per-country counts from the trials whose detail has loaded.
  const { sites, people, countryCounts } = useMemo(() => {
    const sites = new Map<string, SiteAgg>();
    const people = new Map<string, PersonAgg>();
    const countryCounts = new Map<string, { sites: number; trials: Set<string>; recruiting: number; keys: Set<string> }>();
    for (const r of done) {
      const d = detail[r.nct]?.data; if (!d) continue;
      const tinfo: TrialInfo = { id: r.nct, name: r.trial || r.nct, sponsor: r.sponsor, phase: r.phase, regimen: r.regimen, tier: r.tier, status: regSummary[r.nct]?.status };
      const tp = peopleOf(d as any);
      for (const l of d.locations || []) {
        if (!l.facility) continue;
        const k = siteKey(l);
        const rawCountry = l.country || '';
        const country = rawCountry ? (countryCanon?.get(rawCountry.toLowerCase()) || rawCountry) : '';
        if (!sites.has(k)) sites.set(k, { key: k, facility: l.facility, city: l.city || '', state: l.state || '', country, zip: l.zip || '', trials: new Map(), people: new Set(), recruiting: 0, byTrial: new Map() });
        const s = sites.get(k)!;
        if (!s.trials.has(tinfo.id)) { s.trials.set(tinfo.id, tinfo); if (l.status === 'RECRUITING') s.recruiting++; }
        if (!s.byTrial.has(tinfo.id)) s.byTrial.set(tinfo.id, { status: l.status || '', contacts: new Set() });
        (l.contacts || []).forEach((c) => {
          const name = typeof c === 'string' ? c : c?.name;
          if (!name) return;
          const role = typeof c === 'string' ? '' : (c?.role || '');
          s.byTrial.get(tinfo.id)!.contacts.add(role ? `${name} (${role})` : name);
        });
        const cn = country || 'Not stated';
        if (!countryCounts.has(cn)) countryCounts.set(cn, { sites: 0, trials: new Set(), recruiting: 0, keys: new Set() });
        const cs = countryCounts.get(cn)!;
        cs.trials.add(tinfo.id);
        if (!cs.keys.has(k)) { cs.keys.add(k); cs.sites++; }
        if (l.status === 'RECRUITING') cs.recruiting++;
        for (const e of tp) {
          if ((e as any).loc !== l) continue;
          s.people.add(e.name);
          const pk = nameKey(e.name);
          if (!people.has(pk)) people.set(pk, { name: e.name, trials: new Map(), sites: new Map(), roles: new Map(), rank: 9 });
          const P = people.get(pk)!;
          P.trials.set(tinfo.id, tinfo); P.sites.set(k, s);
          if (!P.roles.has(e.label)) P.roles.set(e.label, e.label);
          P.rank = Math.min(P.rank, KIND_RANK[e.kind] ?? 9);
        }
      }
    }
    // Officials / central contacts (study-level, no site) still count as people.
    for (const r of done) {
      const d = detail[r.nct]?.data; if (!d) continue;
      const tinfo = { id: r.nct, name: r.trial || r.nct };
      for (const e of peopleOf(d as any)) {
        if (e.kind !== 'official' && e.kind !== 'central') continue;
        const pk = nameKey(e.name);
        if (!people.has(pk)) people.set(pk, { name: e.name, trials: new Map(), sites: new Map(), roles: new Map(), rank: 9 });
        const P = people.get(pk)!;
        P.trials.set(tinfo.id, tinfo);
        if (!P.roles.has(e.label)) P.roles.set(e.label, e.label);
        P.rank = Math.min(P.rank, KIND_RANK[e.kind] ?? 9);
      }
    }
    return { sites, people, countryCounts };
  }, [done, detail, regSummary, countryCanon]);

  // Offline-gazetteer city coordinates for every aggregated site (independent of the country/search/
  // multi filters below, same as countryCounts — type/agent/study facets reshape the map, but Where/
  // search/In-2+-trials don't shrink the countries or cities it draws).
  const siteXY = useMemo(() => {
    const m = new Map<string, [number, number]>();
    if (!geoCtx) return m;
    for (const s of sites.values()) {
      const xy = resolveSiteXY(s, geoCtx);
      if (xy) m.set(s.key, xy);
    }
    return m;
  }, [sites, geoCtx]);
  const cityKeyOf = (xy: [number, number]) => `${xy[0].toFixed(2)},${xy[1].toFixed(2)}`;

  const sitePass = (s: SiteAgg) =>
    (!country || (s.country || 'Not stated') === country) && (!multi || s.trials.size > 1) &&
    (!q.trim() || `${s.facility} ${s.city} ${s.state} ${s.country} ${[...s.people].join(' ')}`.toLowerCase().includes(q.trim().toLowerCase())) &&
    (!cityFilter || (() => { const xy = siteXY.get(s.key); return !!xy && cityKeyOf(xy) === cityFilter.key; })());
  const visSites = useMemo(() => [...sites.values()].filter(sitePass).sort((a, b) => b.trials.size - a.trials.size || a.facility.localeCompare(b.facility)), [sites, country, multi, q, cityFilter, siteXY]);
  const visPeople = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return [...people.values()]
      .filter((p) => (!country || [...p.sites.values()].some((s) => (s.country || 'Not stated') === country)) && (!multi || p.trials.size > 1) &&
        (!ql || `${p.name} ${[...p.sites.values()].map((s) => s.facility).join(' ')}`.toLowerCase().includes(ql)))
      .sort((a, b) => b.trials.size - a.trials.size || a.rank - b.rank || a.name.localeCompare(b.name));
  }, [people, country, multi, q]);

  const countryOptions = useMemo(() => [...countryCounts.entries()].sort((a, b) => b[1].sites - a[1].sites), [countryCounts]);
  const nAct = (types.size === 3 ? 0 : 1) + (agentSel ? 1 : 0) + (targetSel ? 1 : 0) + (modSel ? 1 : 0) + (phaseSel ? 1 : 0) + (sponsorSel ? 1 : 0) +
    (selectedStudies.size ? 1 : 0) + (country ? 1 : 0) + (q.trim() ? 1 : 0) + (multi ? 1 : 0);
  const resetAll = () => {
    setTypes(new Set([1, 2, 3])); setAgentSel(''); setTargetSel(''); setModSel(''); setPhaseSel(''); setSponsorSel('');
    setSelectedStudies(new Set()); setCountry(''); setQ(''); setMulti(false); setCityFilter(null);
  };

  if (!calRows.length) {
    return <div className="state"><h2>No trials with an NCT# in this setting</h2><p>Sites are built from the trials on this setting&rsquo;s calendar.</p></div>;
  }

  return (
    <>
      <section className="sfp" aria-label="Study site filters">
        <div className="sfp-h">
          <span className="sfp-t">Filters</span>
          {nAct > 0 && <span className="fbadge">{nAct}</span>}
          <button type="button" className={`btn sfp-reset${nAct ? ' on' : ''}`} disabled={!nAct} onClick={resetAll}>&#8634; Reset filters</button>
          <div className="spacer" />
          <span className="meta">
            {done.length < trials.length
              ? `${done.length} of ${trials.length} trials loaded${pendingN ? ` · ${pendingN} being fetched…` : ''}${failedN ? ` · ${failedN} not available` : ''}`
              : `${trials.length} trials read${failedN ? `, ${failedN} not available` : ''}`}
          </span>
        </div>
        <div className="sfp-g">
          <div className="sf-row">
            <span className="sf-l">Type</span>
            <div className="sf-c">
              {[1, 2, 3].filter((t) => calRows.some((r) => r.tier === t)).map((t) => (
                <button
                  key={t} type="button" className="chip" aria-pressed={types.has(t)}
                  onClick={() => setTypes((prev) => { const n = new Set(prev); if (n.has(t)) { if (n.size > 1) n.delete(t); } else n.add(t); return n; })}
                >
                  <span className="sw" style={{ background: `var(--t${t})` }} />{TIER_NAME[t]} <em className="chipn">{calRows.filter((r) => r.tier === t).length}</em>
                </button>
              ))}
            </div>
          </div>
          <div className="sf-row">
            <span className="sf-l">Drug</span>
            <div className="sf-c">
              <Select value={agentSel} onChange={setAgentSel} allLabel="All agents" opts={agentOpts} />
              <Select value={targetSel} onChange={setTargetSel} allLabel="All targets" opts={targetOpts} />
              <Select value={modSel} onChange={setModSel} allLabel="All modalities" opts={modOpts} />
              <Select value={phaseSel} onChange={setPhaseSel} allLabel="All phases" opts={phaseOpts} />
              <Select value={sponsorSel} onChange={setSponsorSel} allLabel="All sponsors" opts={sponsorOpts} />
              <span className="meta">{trials.length} of {trialsByType.length} trials</span>
            </div>
          </div>
          <div className="sf-row">
            <span className="sf-l">Studies</span>
            <div className="sf-c">
              <StudyChips trials={trials} selected={selectedStudies} onToggle={(nct) => setSelectedStudies((prev) => {
                const n = new Set(prev); if (n.has(nct)) n.delete(nct); else n.add(nct); return n;
              })} onClear={() => setSelectedStudies(new Set())} siteCountFor={siteCountFor} />
            </div>
          </div>
          <div className="sf-row">
            <span className="sf-l">Where</span>
            <div className="sf-c">
              <select className="pill" value={country} onChange={(e) => setCountry(e.target.value)} aria-label="Country">
                <option value="">All countries ({countryOptions.length})</option>
                {countryOptions.map(([name, v]) => <option key={name} value={name}>{name} ({v.sites})</option>)}
              </select>
              <input className="search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a site, city or person" aria-label="Find site or person" />
              <button type="button" className="chip" aria-pressed={multi} onClick={() => setMulti((v) => !v)}>In 2+ trials</button>
            </div>
          </div>
        </div>
      </section>

      {done.length < trials.length && (
        <div className="progress" aria-hidden="true"><i style={{ width: `${(done.length / Math.max(1, trials.length)) * 100}%` }} /></div>
      )}

      <div className="wm-head">
        <span className="cap">Global site footprint</span>
        {country && <button type="button" className="chip" aria-pressed="true" onClick={() => setCountry('')}>{country} &#10005;</button>}
        <div className="spacer" />
        <button type="button" className="linkbtn" onClick={() => setShowMap((v) => !v)}>{showMap ? 'Hide map' : 'Show map'}</button>
      </div>

      {showMap && (
        geoError ? (
          <div className="wm-empty meta">Map data couldn&rsquo;t be loaded; the site list below still works.</div>
        ) : !geo ? (
          <div className="wm-empty meta">Loading map&hellip;</div>
        ) : countryCounts.size === 0 ? (
          <div className="wm-empty meta">{done.length ? 'No sites match these filters.' : 'Loading registry…'}</div>
        ) : (
          <WorldMap
            geo={geo} hi={hi} countryCounts={countryCounts} selected={country} onSelect={(n) => setCountry((c) => (c === n ? '' : n))}
            sites={sites} siteXY={siteXY} cityFilter={cityFilter}
            onCityClick={(key, label) => setCityFilter((c) => (c?.key === key ? null : { key, label }))}
          />
        )
      )}

      {cityFilter && (
        <div className="citybar">
          <span>Showing {visSites.length} site{visSites.length === 1 ? '' : 's'} in {cityFilter.label}</span>
          <button type="button" className="btn ghost" onClick={() => setCityFilter(null)}>Show all sites</button>
        </div>
      )}

      <div className="tblbar sf-listbar">
        <div className="seg" role="group" aria-label="List">
          {([['sites', `Sites (${visSites.length})`], ['investigators', `Investigators (${visPeople.length})`]] as const).map(([k, l]) => (
            <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
      </div>

      {tab === 'sites' ? (
        <SitesTable sites={visSites.slice(0, limit)} total={visSites.length} openSite={openSite} setOpenSite={setOpenSite} onMore={() => setLimit((v) => v + 60)} />
      ) : (
        <PeopleTable people={visPeople.slice(0, limit)} total={visPeople.length} onMore={() => setLimit((v) => v + 60)} />
      )}
      <p className="note">
        Built from the site lists of the selected trial types on this setting&rsquo;s calendar, read from ClinicalTrials.gov. Investigator counts run below
        site counts because ClinicalTrials.gov drops site contacts once a site stops recruiting, and many industry trials list only a central sponsor contact.
      </p>
    </>
  );
};

const Select: React.FC<{ value: string; onChange: (v: string) => void; allLabel: string; opts: [string, number][] }> = ({ value, onChange, allLabel, opts }) => (
  <select className="pill" value={value} onChange={(e) => onChange(e.target.value)} aria-label={allLabel}>
    <option value="">{allLabel} ({opts.length})</option>
    {opts.map(([v, n]) => <option key={v} value={v}>{v} ({n})</option>)}
  </select>
);

/** The "Studies" row: a horizontally-scrolling, independently-multiselectable chip per trial (picking one
 * or more narrows the sites/table below to just those studies), with an "All" chip to clear the selection. */
const StudyChips: React.FC<{
  trials: (ArCalendarRow & { nct: string })[]; selected: Set<string>; onToggle: (nct: string) => void; onClear: () => void;
  siteCountFor: (r: ArCalendarRow & { nct: string }) => number | null;
}> = ({ trials, selected, onToggle, onClear, siteCountFor }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollBy = (dir: 1 | -1) => {
    const el = scrollRef.current; if (!el) return;
    el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.8), behavior: 'smooth' });
  };
  return (
    <div className="frow sturow" role="group" aria-label="Studies">
      <span className="fl cap">Studies</span>
      <button type="button" className="chip" aria-pressed={!selected.size} onClick={onClear}>
        All <span className="chipn">{trials.length}</span>
      </button>
      <button type="button" className="stu-arr" aria-label="Scroll studies left" title="Scroll studies left" onClick={() => scrollBy(-1)}>&#8249;</button>
      <div className="stu-scroll" ref={scrollRef}>
        {trials.map((r) => {
          const n = siteCountFor(r);
          return (
            <button
              key={r.nct} type="button" className="chip" aria-pressed={selected.has(r.nct)}
              title={`${r.nct}${r.sponsor ? ' · ' + r.sponsor : ''}${r.phase ? ' · ' + r.phase : ''}${r.regimen ? ' · ' + r.regimen : ''}`}
              onClick={() => onToggle(r.nct)}
            >
              <span>{r.trial || r.nct}</span> <span className="chipn">{n != null ? n : '…'}</span>
            </button>
          );
        })}
      </div>
      <button type="button" className="stu-arr" aria-label="Scroll studies right" title="Scroll studies right" onClick={() => scrollBy(1)}>&#8250;</button>
    </div>
  );
};

type Box = [number, number, number, number];

function clampBox([x, y, w0]: Box, W: number, H: number): Box {
  const w = Math.min(W, Math.max(36, w0));
  const h = (w * H) / W;
  const cx = Math.min(W - w, Math.max(0, x));
  const cy = Math.min(H - h, Math.max(0, y));
  return [cx, cy, w, h];
}
function fitBox([bx, by, bw, bh]: Box, W: number, H: number): Box {
  const w = Math.max(bw * 1.5, bh * 1.5 * (W / H), 60);
  const cx = bx + bw / 2, cy = by + bh / 2;
  return clampBox([cx - w / 2, cy - (w * H / W) / 2, w, (w * H) / W], W, H);
}
function boxesClose(a: Box, b: Box, eps = 1): boolean {
  return Math.abs(a[0] - b[0]) < eps && Math.abs(a[1] - b[1]) < eps && Math.abs(a[2] - b[2]) < eps;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const WorldMap: React.FC<{
  geo: { wmap: ArGeoWmap; lookups: ArGeoLookups };
  hi: ArGeoHi | null;
  countryCounts: Map<string, { sites: number; trials: Set<string>; recruiting: number }>;
  selected: string; onSelect: (name: string) => void;
  sites: Map<string, SiteAgg>; siteXY: Map<string, [number, number]>;
  cityFilter: { key: string; label: string } | null; onCityClick: (key: string, label: string) => void;
}> = ({ geo, hi, countryCounts, selected, onSelect, sites, siteXY, cityFilter, onCityClick }) => {
  const { w: W, h: H } = geo.wmap;
  const FULL: Box = useMemo(() => [0, 0, W, H], [W, H]);
  const [box, setBox] = useState<Box>(FULL);
  const [dragging, setDragging] = useState(false);
  const boxRef = useRef(box);
  boxRef.current = box;
  const autoCountryRef = useRef<string | null>(null);
  const prevBoxRef = useRef<Box | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  // dragRef is only non-null while the pointer is actually down -- cleared the instant it's released, so
  // a plain hover afterward (no button held) can never be mistaken for a drag. movedRef separately
  // records whether the gesture that just ended was a drag, purely so the trailing click it generates
  // can be suppressed; it's reset a tick after release once that click has had a chance to check it.
  const dragRef = useRef<{ x: number; y: number; box: Box } | null>(null);
  const movedRef = useRef(false);

  const countryBox = (name: string): Box | null => {
    const b = geo.wmap.b[name];
    if (b) return b;
    const pt = geo.wmap.x.find(([n]) => n === name);
    return pt ? [pt[1] - 6, pt[2] - 4, 12, 8] : null;
  };

  // Auto-fit the map to the selected country; restore the prior view when the selection clears.
  useEffect(() => {
    if (selected && autoCountryRef.current !== selected) {
      const bb = countryBox(selected);
      if (bb) {
        if (!autoCountryRef.current) prevBoxRef.current = boxRef.current;
        setBox(fitBox(bb, W, H));
      }
      autoCountryRef.current = selected;
    } else if (!selected && autoCountryRef.current) {
      setBox(prevBoxRef.current || FULL);
      autoCountryRef.current = null;
      prevBoxRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, geo]);

  const max = Math.max(1, ...[...countryCounts.values()].map((v) => v.sites));
  const ranked = [...countryCounts.entries()].filter(([n]) => n !== 'Not stated').sort((a, b) => b[1].sites - a[1].sites);
  const entries = geo.wmap.c.map(([name, d, cx, cy]) => ({ name, d, cx, cy, hit: countryCounts.get(name) }));
  const points = geo.wmap.x.filter(([name]) => countryCounts.has(name)).map(([name, cx, cy]) => ({ name, cx, cy, hit: countryCounts.get(name)! }));
  // Choropleth: opacity scales with site count on a log curve rather than a flat fill for every "has
  // sites" country.
  const opacityFor = (n: number) => +(0.16 + 0.62 * Math.log1p(n) / Math.log1p(max)).toFixed(2);

  // City dots (only shown once zoomed in): group every aggregated site that the offline gazetteer
  // could resolve by its matched [x,y], independent of the country/search/multi filters (same source
  // countryCounts draws from), so the dot layer doesn't flicker as those filters change.
  const cityGroups = useMemo(() => {
    const m = new Map<string, { x: number; y: number; sites: SiteAgg[] }>();
    for (const s of sites.values()) {
      const xy = siteXY.get(s.key);
      if (!xy) continue;
      const key = `${xy[0].toFixed(2)},${xy[1].toFixed(2)}`;
      if (!m.has(key)) m.set(key, { x: xy[0], y: xy[1], sites: [] });
      m.get(key)!.sites.push(s);
    }
    return m;
  }, [sites, siteXY]);
  const cmax = Math.max(1, ...[...cityGroups.values()].map((g) => g.sites.length));
  const unplacedCount = [...sites.values()].filter((s) => !siteXY.has(s.key)).length;

  const toggle = (name: string) => { if (!movedRef.current) onSelect(name); };
  const k = W / box[2];
  const near = k >= 2.4;
  // Dot radius and label font are divided back down by the zoom factor so marks stay a stable visual
  // size instead of ballooning as the viewBox shrinks.
  const scaleR = (r0: number) => r0 / Math.pow(k, 0.75);
  const scaleFont = (f0: number) => f0 / Math.pow(k, 0.9);
  const labelShown = k > 2.5 ? 40 : k > 1.4 ? 16 : 8;
  const rankedDots = [...entries.filter((e): e is typeof e & { hit: NonNullable<typeof e['hit']> } => !!e.hit), ...points]
    .sort((a, b) => b.hit.sites - a.hit.sites);
  const labelRank = new Map(rankedDots.map((d, i) => [d.name, i]));
  const zoomAt = (factor: number, px?: number, py?: number) => {
    const z = boxRef.current;
    const w = z[2] / factor;
    const [cx, cy] = px == null ? [z[0] + z[2] / 2, z[1] + z[3] / 2] : [px, py as number];
    const rx = px == null ? 0.5 : (cx - z[0]) / z[2], ry = px == null ? 0.5 : (cy - z[1]) / z[3];
    setBox(clampBox([cx - w * rx, cy - (w * H / W) * ry, w, (w * H) / W], W, H));
  };
  const toSvg = (clientX: number, clientY: number): [number, number] => {
    const rc = svgRef.current?.getBoundingClientRect();
    if (!rc) return [0, 0];
    const z = boxRef.current;
    return [z[0] + ((clientX - rc.left) / rc.width) * z[2], z[1] + ((clientY - rc.top) / rc.height) * z[3]];
  };

  const regionNow = (): string => {
    if (boxesClose(box, FULL)) return 'World';
    for (const [n, b] of Object.entries(geo.wmap.r)) if (boxesClose(box, fitBox(b, W, H))) return n;
    return '';
  };
  const cur = regionNow();

  return (
    <div className="wm">
      <div className="wm-mapw">
        <div className="wm-tools">
          <div className="wm-reg" role="group" aria-label="Zoom to region">
            <button type="button" className="wm-rb" aria-pressed={cur === 'World'} onClick={() => { setBox(FULL); if (selected) autoCountryRef.current = selected; }}>World</button>
            {Object.entries(geo.wmap.r).map(([n, b]) => (
              <button key={n} type="button" className="wm-rb" aria-pressed={cur === n} onClick={() => { setBox(fitBox(b, W, H)); if (selected) autoCountryRef.current = selected; }}>{n}</button>
            ))}
          </div>
          <div className="wm-zb">
            <button type="button" className="wm-z" aria-label="Zoom in" title="Zoom in" onClick={() => zoomAt(1.6)}>+</button>
            <button type="button" className="wm-z" aria-label="Zoom out" title="Zoom out" onClick={() => zoomAt(1 / 1.6)}>&minus;</button>
            <span className="wm-zl">{k > 1.05 ? `${k.toFixed(1)}×` : ''}</span>
          </div>
        </div>
        <svg
          ref={svgRef}
          viewBox={box.map((v) => v.toFixed(1)).join(' ')}
          className={`wm-svg${k >= 1.6 ? ' zin' : ''}${near ? ' near' : ''}${dragging ? ' drag' : ''}`}
          onPointerDown={(e) => { dragRef.current = { x: e.clientX, y: e.clientY, box: boxRef.current }; movedRef.current = false; }}
          onPointerMove={(e) => {
            const d = dragRef.current; if (!d) return;
            const dx = e.clientX - d.x, dy = e.clientY - d.y;
            if (!movedRef.current && Math.hypot(dx, dy) < 4) return;
            if (!movedRef.current) { movedRef.current = true; setDragging(true); try { svgRef.current?.setPointerCapture(e.pointerId); } catch { /* ignore */ } }
            const rc = svgRef.current?.getBoundingClientRect();
            if (!rc) return;
            setBox(clampBox([d.box[0] - (dx / rc.width) * d.box[2], d.box[1] - (dy / rc.height) * d.box[3], d.box[2], d.box[3]], W, H));
          }}
          onPointerUp={() => { setDragging(false); dragRef.current = null; setTimeout(() => { movedRef.current = false; }, 0); }}
          onPointerCancel={() => { setDragging(false); dragRef.current = null; movedRef.current = false; }}
          onDoubleClick={(e) => { e.preventDefault(); const [x, y] = toSvg(e.clientX, e.clientY); zoomAt(e.shiftKey ? 0.5 : 2, x, y); }}
          onWheel={(e) => { if (!(e.ctrlKey || e.metaKey)) return; e.preventDefault(); const [x, y] = toSvg(e.clientX, e.clientY); zoomAt(e.deltaY < 0 ? 1.25 : 0.8, x, y); }}
        >
          <g>
            {entries.map((e) => {
              const isSel = selected === e.name;
              return (
                <path
                  key={e.name} d={e.d}
                  className={`wm-c${e.hit ? ' has' : ''}${isSel ? ' sel' : ''}`}
                  // A selected country stays clearly visible even at a low site count, where the
                  // choropleth's log-scaled opacity alone would otherwise fade it to near-invisible
                  // once zoomed in close (it reads as "the country disappeared").
                  style={e.hit ? { fillOpacity: isSel ? Math.max(opacityFor(e.hit.sites), 0.45) : opacityFor(e.hit.sites) } : undefined}
                  onClick={() => e.hit && toggle(e.name)}
                >
                  <title>{e.hit ? `${e.name}: ${plural(e.hit.sites, 'site')} · ${plural(e.hit.trials.size, 'trial')}${e.hit.recruiting ? ` · ${e.hit.recruiting} recruiting` : ''}` : e.name}</title>
                </path>
              );
            })}
          </g>
          {hi && <path className="wm-adm" d={hi.adm} />}
          {rankedDots.map((p) => {
            const r0 = 3 + 12 * Math.sqrt(p.hit.sites / max);
            const isSel = selected === p.name;
            const rank = labelRank.get(p.name) ?? Infinity;
            return (
              <g key={`b-${p.name}`}>
                {rank < labelShown && (
                  <text x={p.cx} y={p.cy} className="wm-lab" textAnchor="middle" dy={-(scaleR(r0) + 3 / k)} fontSize={scaleFont(10)} style={{ pointerEvents: 'none' }}>{p.hit.sites}</text>
                )}
                <circle
                  cx={p.cx} cy={p.cy} r={scaleR(r0)}
                  className={`wm-dot${isSel ? ' sel' : ''}`}
                  // Once zoomed in close (k>=2.4, "near"/city-dot mode), CSS fades every country dot to
                  // 12% opacity AND sets pointer-events:none on it — meant to hand off interaction to
                  // city dots, but it also kills the ability to click a dot-only country (Singapore,
                  // Macau, etc. — no polygon shape, so the dot *is* the country) back off, or to switch
                  // to a different one, for as long as you're zoomed in that far. Keep every country dot
                  // clickable regardless of zoom, and keep the selected one clearly visible too.
                  style={{ pointerEvents: 'auto', ...(isSel ? { fillOpacity: 0.9 } : null) }}
                  onClick={() => toggle(p.name)}
                >
                  <title>{`${p.name}: ${plural(p.hit.sites, 'site')} · ${plural(p.hit.trials.size, 'trial')}`}</title>
                </circle>
              </g>
            );
          })}
          {[...cityGroups.entries()].map(([key, g]) => {
            const names = g.sites.slice().sort((a, b) => b.trials.size - a.trials.size).map((s) => `${s.facility} (${s.trials.size})`);
            const s0 = g.sites[0];
            const label = [s0.city, s0.country].filter(Boolean).join(', ');
            return (
              <circle
                key={`c-${key}`} cx={g.x} cy={g.y} r={scaleR(1.6 + 5 * Math.sqrt(g.sites.length / cmax))}
                className={`wm-city${cityFilter?.key === key ? ' sel' : ''}`}
                onClick={() => { if (!movedRef.current) onCityClick(key, label); }}
              >
                <title>{`${[s0.city, s0.state, s0.country].filter(Boolean).join(', ')}: ${plural(g.sites.length, 'site')}\n${names.slice(0, 8).join('\n')}${names.length > 8 ? `\n+${names.length - 8} more` : ''}`}</title>
              </circle>
            );
          })}
        </svg>
        <div className="wm-hint meta">
          Drag to pan · double-click or + to zoom in · Ctrl/&#8984; + scroll to zoom ·{' '}
          {near
            ? `Dots are cities with sites (${cityGroups.size} ${cityGroups.size === 1 ? 'city' : 'cities'}${unplacedCount ? `; ${plural(unplacedCount, 'site')} couldn't be placed` : ''}). Click one to list its sites.`
            : 'Zoom in to see sites by city.'}
        </div>
      </div>
      <div className="wm-list">
        <div className="wm-lh">{ranked.length} countries</div>
        {ranked.map(([name, v]) => (
          <button key={name} type="button" onClick={() => onSelect(name)} className={`wm-row${selected === name ? ' sel' : ''}`} title={`${name}: ${plural(v.sites, 'site')} · ${plural(v.trials.size, 'trial')}${v.recruiting ? ` · ${v.recruiting} recruiting` : ''}`}>
            <span className="wm-n">{name}</span>
            <span className="wm-bar"><i style={{ width: `${(v.sites / max) * 100}%` }} /></span>
            <span className="wm-v">{v.sites} · {v.trials.size}T</span>
          </button>
        ))}
        <div className="meta" style={{ padding: '6px 2px 0' }}>Sites · trials. Select a country to zoom to it and filter the list below.</div>
      </div>
    </div>
  );
};

const SitesTable: React.FC<{ sites: SiteAgg[]; total: number; openSite: string | null; setOpenSite: (k: string | null) => void; onMore: () => void }> = ({ sites, total, openSite, setOpenSite, onMore }) => (
  <div className="resw">
    {!sites.length ? <p className="meta" style={{ padding: 12 }}>No sites match.</p> : (
      <table className="res">
        <thead><tr>{['Site', 'Site contacts / PIs', 'Location', 'Trials', 'Recruiting', 'Trial names'].map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
        <tbody>
          {sites.map((s) => {
            const open = openSite === s.key;
            return (
              <React.Fragment key={s.key}>
                <tr className={`srow${open ? ' open' : ''}`}>
                  <td>
                    <div className="scell">
                      <button type="button" className="sx" aria-expanded={open} onClick={() => setOpenSite(open ? null : s.key)}>
                        <span className="sx-c" aria-hidden="true">{open ? '▾' : '▸'}</span>
                        <b>{s.facility}</b>
                      </button>
                    </div>
                  </td>
                  <td>{s.people.size ? [...s.people].join('; ') : '—'}</td>
                  <td>{[s.city, s.state, s.country].filter(Boolean).join(', ')}</td>
                  <td className="num">{s.trials.size}</td>
                  <td className="num">{s.recruiting || '—'}</td>
                  <td>
                    <div className="inl">
                      {[...s.trials.values()].map((t) => (
                        <a key={t.id} className="tchip" href={`https://clinicaltrials.gov/study/${t.id}`} target="_blank" rel="noopener noreferrer" title={`${t.id}${t.sponsor ? ' · ' + t.sponsor : ''}${t.phase ? ' · ' + t.phase : ''}`}>{t.name}</a>
                      ))}
                    </div>
                  </td>
                </tr>
                {open && <tr className="sdet"><td colSpan={6}><SiteDetail s={s} /></td></tr>}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    )}
    {total > sites.length && <div style={{ textAlign: 'center', margin: 12 }}><button type="button" className="btn" onClick={onMore}>Show more ({total - sites.length} left)</button></div>}
  </div>
);

const SiteDetail: React.FC<{ s: SiteAgg }> = ({ s }) => {
  const addr = [s.facility, s.city, s.state, s.zip, s.country].filter(Boolean).join(', ');
  const contacts = useMemo(() => {
    const all = new Map<string, { name: string; kind: string; trials: Set<string> }>();
    for (const [id, bt] of s.byTrial) {
      for (const raw of bt.contacts) {
        const nameOnly = raw.replace(/\s*\([^)]*\)\s*$/, '');
        const cleaned = cleanPerson(nameOnly);
        const key = (cleaned || raw).toLowerCase();
        if (!all.has(key)) all.set(key, { name: cleaned || raw, kind: cleaned ? 'Site contact / PI' : 'Central or sponsor contact', trials: new Set() });
        all.get(key)!.trials.add(id);
      }
    }
    return [...all.values()].sort((a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name));
  }, [s]);

  return (
    <div className="sd">
      <div className="sd-grid">
        <div>
          <span className="cap">Address</span>
          <span>{addr || 'NR'} <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`} target="_blank" rel="noopener noreferrer">Map &#8599;</a></span>
        </div>
        <div>
          <span className="cap">Trials here (selected types)</span>
          <span>{s.trials.size} ({s.recruiting} recruiting at this site)</span>
        </div>
        <div>
          <span className="cap">Find the site&rsquo;s other trials</span>
          <a href={`https://clinicaltrials.gov/search?locStr=${encodeURIComponent([s.city, s.state, s.country].filter(Boolean).join(', '))}&term=${encodeURIComponent(s.facility)}`} target="_blank" rel="noopener noreferrer">Search ClinicalTrials.gov &#8599;</a>
        </div>
      </div>
      <div className="sd-sec">
        <span className="cap">Contacts listed</span>
        {contacts.length ? (
          <ul className="sd-list">
            {contacts.map((c) => (
              <li key={c.name}><b>{c.name}</b><small> · {c.kind} · {[...c.trials].map((id) => s.trials.get(id)?.name || id).join(', ')}</small></li>
            ))}
          </ul>
        ) : <span className="meta">No contacts listed for this site on ClinicalTrials.gov.</span>}
      </div>
      <div className="sd-tw">
        <table className="sd-t">
          <thead><tr>{['Trial', 'Sponsor', 'Phase', 'Regimen', 'Trial status', 'Status at this site', 'Contacts at this site'].map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
          <tbody>
            {[...s.trials.entries()].map(([id, t]) => {
              const bt = s.byTrial.get(id);
              return (
                <tr key={id}>
                  <td>
                    <a href={`https://clinicaltrials.gov/study/${id}`} target="_blank" rel="noopener noreferrer">{t.name}</a>
                    <small className="sd-id">{id}{t.tier ? ` · ${TIER_NAME[t.tier]}` : ''}</small>
                  </td>
                  <td>{t.sponsor || 'NR'}</td>
                  <td>{t.phase || 'NR'}</td>
                  <td>{t.regimen || 'NR'}</td>
                  <td>{t.status ? <span className={`rs ${regClass(t.status)}`}>{regLabel(t.status)}</span> : 'NR'}</td>
                  <td>{bt?.status ? <span className={`rs ${regClass(bt.status)}`}>{regLabel(bt.status)}</span> : 'Not stated'}</td>
                  <td>{bt?.contacts.size ? [...bt.contacts].join('; ') : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="meta">Names come from the site&rsquo;s contact listing on ClinicalTrials.gov. Phone numbers and emails aren&rsquo;t available through the connector; open a trial&rsquo;s registry page for full contact details.</p>
    </div>
  );
};

const PeopleTable: React.FC<{ people: PersonAgg[]; total: number; onMore: () => void }> = ({ people, total, onMore }) => (
  <div className="resw">
    {!people.length ? <p className="meta" style={{ padding: 12 }}>No investigators match. Many industry trials list only a central contact, not site PIs.</p> : (
      <table className="res">
        <thead><tr>{['Investigator / contact', 'Role', 'Trials', 'Sites', 'Trial names'].map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.name}>
              <td><b>{p.name}</b></td>
              <td className="roles">{[...p.roles.values()].slice(0, 2).map((lb, i) => <span key={i} className="prole">{lb}</span>)}</td>
              <td className="num">{p.trials.size}</td>
              <td>{[...p.sites.values()].map((s) => s.facility).join('; ') || '—'}</td>
              <td>
                <div className="inl">
                  {[...p.trials.values()].map((t) => (
                    <a key={t.id} className="tchip" href={`https://clinicaltrials.gov/study/${t.id}`} target="_blank" rel="noopener noreferrer">{t.name}</a>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
    {total > people.length && <div style={{ textAlign: 'center', margin: 12 }}><button type="button" className="btn" onClick={onMore}>Show more ({total - people.length} left)</button></div>}
  </div>
);

export default StudySitesTab;
